import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { Tooltip, Alert, TextField } from "@mui/material";
import DraftEditor from "../Editor/DraftEditor";
import apiUrl from "../api";
import "./offerForm.css";

const OfferForm = (props) => {
  const [offerData, setOfferData] = useState({
    name: "",
    description: EditorState.createEmpty(),
    discount: 0,
    start_date: "",
    end_date: "",
    properties: [],
  });
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));
  
  // Initialize form with existing data if in edit mode
  useEffect(() => {
    if (props.editMode && props.offerData) {
      // Convert HTML description to Draft.js EditorState
      let editorState = EditorState.createEmpty();
      if (props.offerData.description) {
        const blocksFromHtml = htmlToDraft(props.offerData.description);
        if (blocksFromHtml) {
          const { contentBlocks, entityMap } = blocksFromHtml;
          const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
          editorState = EditorState.createWithContent(contentState);
        }
      }
      
      // Format the date strings for input fields
      const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };
      
      // Map property IDs to property objects
      const mappedProperties = props.offerData.properties ? 
        props.offerData.properties.map(property => ({
          value: property,
          label: property.name
        })) : [];
      
      // Set form data with existing offer data
      setOfferData({
        name: props.offerData.name || "",
        description: editorState,
        discount: props.offerData.discount || 0,
        start_date: formatDateForInput(props.offerData.start_date),
        end_date: formatDateForInput(props.offerData.end_date),
        properties: mappedProperties,
        _id: props.offerData._id
      });
    }
  }, [props.editMode, props.offerData]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setOfferData({ ...offerData, [name]: value });
  };

  const handleDescriptionChange = (editorState) => {
    setOfferData({ ...offerData, description: editorState });
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/property/getPropertiesNames`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setProperties(response.data.propertiesNames);
    } catch (error) {
      console.error("Failed to fetch properties: ", error);
      setErrorMessage("Failed to fetch properties");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (offerData.name === "") {
      setIsLoading(false);
      setErrorMessage("Name is required.");
      return;
    }

    const dataToSend = {
      name: offerData.name,
      description: draftToHtml(
        convertToRaw(offerData.description.getCurrentContent())
      ),
      discount: parseFloat(offerData.discount) || 0,
      start_date: offerData.start_date || null,
      end_date: offerData.end_date || null,
      properties: offerData.properties.map(property => property.value._id)
    };

    try {
      let response;
      
      // If in edit mode, update the existing offer
      if (props.editMode && offerData._id) {
        response = await axios.put(`${apiUrl}/offer/${offerData._id}`, dataToSend, {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        });
        setSuccessMessage("Offer updated successfully!");
      } else {
        // Otherwise create a new offer
        response = await axios.post(`${apiUrl}/offer`, dataToSend, {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        });
        setSuccessMessage("Offer created successfully!");
      }

      setIsLoading(false);
      if (props.onSuccess) {
        props.onSuccess(response.data.data);
      }
      setTimeout(() => {
        props.onClose && props.onClose();
      }, 1500);
    } catch (error) {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || `Failed to ${props.editMode ? "update" : "create"} offer.`);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  return (
    <div className="offer-form-wrapper">
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <form className="offer-form" onSubmit={handleSubmit}>
          {/* Name - only required field */}
          <div className="form-group">
            <div className="form-field">
              <label className="form-field-label" htmlFor="name">
                Name*:
              </label>
              <input
                className="form-field-input"
                type="text"
                id="name"
                name="name"
                value={offerData.name}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* Description - not required */}
          <div className="form-group">
            <div className="form-field">
              <label htmlFor="description" className="form-field-label">
                Description:
              </label>
              <div className="field-subtitle">
                Enter detailed information about the offer. This description will be visible to guests.
              </div>
              <DraftEditor
                editorState={offerData.description}
                setEditorState={handleDescriptionChange}
              />
            </div>
          </div>

          {/* Properties - not required */}
          <div className="form-group">
            <div className="form-field">
              <label htmlFor="properties" className="form-field-label">
                Properties:
              </label>
              <Select
                id="properties"
                name="properties"
                value={offerData.properties}
                onChange={(selectedOptions) => {
                  setOfferData({
                    ...offerData,
                    properties: selectedOptions,
                  });
                }}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "var(--secondary-color)",
                    primary25: "var(--tertiary-color)",
                  },
                })}
                options={
                  properties &&
                  properties.map((property) => ({
                    value: property,
                    label: property.name,
                  }))
                }
                isMulti
              />
            </div>
          </div>

          {/* Discount */}
          <div className="form-group">
            <div className="form-field">
              <label className="form-field-label" htmlFor="discount">
                Discount (%):
              </label>
              <div className="field-subtitle">
                The percentage discount that will be applied to the property price. This will be displayed to guests.
              </div>
              <input
                className="form-field-input"
                type="number"
                id="discount"
                name="discount"
                min="0"
                max="100"
                step="0.1"
                value={offerData.discount}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Start Date & End Date */}
          <div className="form-group">
            <div className="form-field">
              <label htmlFor="start_date" className="form-field-label">
                Start Date:
              </label>
              <input
                id="start_date"
                name="start_date"
                type="date"
                className="form-field-input"
                value={offerData.start_date}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-field">
              <label htmlFor="end_date" className="form-field-label">
                End Date:
              </label>
              <input
                id="end_date"
                name="end_date"
                type="date"
                className="form-field-input"
                value={offerData.end_date}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {errorMessage && (
            <Alert severity="error" sx={{ marginBottom: "10px" }}>
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ marginBottom: "10px" }}>
              {successMessage}
            </Alert>
          )}

          {/* Submit Button */}
          <div className="form-group">
            <div className="form-field">
              <button
                type="submit"
                className="add-offer-button__button"
                style={{
                  backgroundColor: props.color || "var(--primary-color)",
                  color: props.textColor || "var(--primary-text-color)",
                  fontSize: props.fontSize || "15px",
                  fontWeight: props.fontWeight || "600",
                  width: props.width || "100%",
                }}
                disabled={isLoading}
              >
                {isLoading ? 
                  (props.editMode ? "Updating..." : "Creating...") : 
                  (props.editMode ? "Update Offer" : "Create Offer")
                }
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default OfferForm;
